import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faPencil, faBan, faUsers, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FormField } from '../../../../interfaces/form-field.model';
import { AccessoryService } from 'src/app/services/vehicle/accessory.service';
import { NotifierService } from 'angular-notifier';
import { getEmptyGuid, isEmptyGuid } from '../../../../services/util.service';

@Component({
  selector: 'app-accessory-detail',
  templateUrl: './accessory-detail.component.html',
  styleUrls: ['./accessory-detail.component.css']
})
export class AccessoryDetailComponent implements OnInit {
  faPencil = faPencil;
  faBan = faBan;
  faPlus = faPlus;
  faUsers = faUsers;

  mode: string = "Add";
  accessoryId: string = getEmptyGuid();

  formFields: FormField[] = [
    { type: 'text', id: 'accessoryCode', label: 'Accessory Code', value: '', required: true, maxLength: 50 },
    { type: 'text', id: 'description', label: 'Description', value: '', required: true, maxLength: 200 },
    { type: 'number', id: 'costPrice', label: 'Cost Price', value: 0, required: true, min: 0, max: 999999, step: 0.01 },
    { type: 'text', id: 'netPrice', label: 'Nett Price', value: '0', required: true, maxLength: 150 },
    { type: 'text', id: 'retailPrice', label: 'Retail Price', value: '0', required: true, maxLength: 150 },
    { type: 'select', id: 'classification', label: 'Classification', required: true, options: [{ value: 'I', display: 'I' }, { value: 'E', display: 'E' }] },
    { type: 'number', id: 'salesmanPrice', label: 'Salesman Price', value: 0, min: 0, max: 999999, step: 0.01 },
    { type: 'text', id: 'vendorName', label: 'Vendor Name', value: '' },
    { type: 'select', id: 'fitted', label: 'Fitted', required: true, options: [{ value: 'L', display: 'Local' }, { value: 'F', display: 'Factory' }] },
    { type: 'select', id: 'status', label: 'Status', required: true, options: [{ value: '1', display: 'Active' }, { value: '0', display: 'Inactive' }] }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accessoryService: AccessoryService,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.accessoryId = params['id'];

      if (!isEmptyGuid(this.accessoryId)) {
        this.mode = 'Edit';
        this.loadAccessoryDetail();
      }
    });
  }

  private loadAccessoryDetail(): void {
    this.accessoryService.getDetail(this.accessoryId).subscribe({
      next: (response) => {
        const data = response.body;
        this.formFields = [
          { type: 'text', id: 'accessoryCode', label: 'Accessory Code', value: data.AccessoryCode, required: true, maxLength: 50 },
          { type: 'text', id: 'description', label: 'Description', value: data.Description, required: true, maxLength: 200 },
          { type: 'number', id: 'costPrice', label: 'Cost Price', value: data.CostPrice, required: true, min: 0, max: 999999, step: 0.01 },
          { type: 'text', id: 'netPrice', label: 'Nett Price', value: String(data.NettPrice ?? ''), required: true, maxLength: 150 },
          { type: 'text', id: 'retailPrice', label: 'Retail Price', value: String(data.RetailPrice ?? ''), required: true, maxLength: 150 },
          {
            type: 'select',
            id: 'classification',
            label: 'Classification',
            value: data.Classification,
            required: true,
            options: [{ value: 'I', display: 'I' }, { value: 'E', display: 'E' }]
          },
          { type: 'number', id: 'salesmanPrice', label: 'Salesman Price', value: data.SalesmanPrice, min: 0, step: 0.01 },
          { type: 'text', id: 'vendorName', label: 'Vendor Name', value: data.Vendor },
          {
            type: 'select',
            id: 'fitted',
            label: 'Fitted',
            value: data.Fitted,
            required: true,
            options: [{ value: 'L', display: 'Local' }, { value: 'F', display: 'Factory' }]
          },
          {
            type: 'select',
            id: 'status',
            label: 'Status',
            value: data.Status,
            required: true,
            options: [{ value: '1', display: 'Active' }, { value: '0', display: 'Inactive' }]
          }
        ];
      },
      error: (error) => {
        console.error("Error loading accessory detail:", error);
        this.notifier.notify('error', 'Error loading accessory detail');
      }
    });
  }

  onSave(): void {
    if (!this.hasRequiredValues()) {
      this.notifier.notify('warning', 'Please fill in all required fields.');
      return;
    }
    if (this.mode === 'Add') {
      this.addAccessory();
    } else if (this.mode === 'Edit') {
      this.updateAccessory();
    }
  }

  private addAccessory(): void {
    const newAccessoryData = {
      AccessoryId: '0',
      AccessoryCode: this.getFieldValue('accessoryCode'),
      CostPrice: Number(this.getFieldValue('costPrice')),
      RetailPrice: String(this.getFieldValue('retailPrice')),
      SalesmanPrice: this.getSalesmanPrice(),
      NettPrice: String(this.getFieldValue('netPrice')),
      Fitted: this.getFieldValue('fitted'),
      Description: this.getFieldValue('description'),
      Classification: this.getFieldValue('classification'),
      Vendor: this.getFieldValue('vendorName'),
      Status: this.getFieldValue('status')
    };

    this.accessoryService.saveDetails(newAccessoryData).subscribe({
      next: (response) => {
        this.handleSaveResponse(response);
      },
      error: (error) => {
        console.error("Error adding accessory:", error);
        this.notifier.notify('error', 'Error adding accessory');
      }
    });
  }

  private updateAccessory(): void {
    const updatedAccessoryData = {
      AccessoryId: this.accessoryId,
      AccessoryCode: this.getFieldValue('accessoryCode'),
      CostPrice: Number(this.getFieldValue('costPrice')),
      RetailPrice: String(this.getFieldValue('retailPrice')),
      SalesmanPrice: this.getSalesmanPrice(),
      NettPrice: String(this.getFieldValue('netPrice')),
      Fitted: this.getFieldValue('fitted'),
      Description: this.getFieldValue('description'),
      Classification: this.getFieldValue('classification'),
      Vendor: this.getFieldValue('vendorName'),
      Status: this.getFieldValue('status')
    };

    this.accessoryService.saveDetails(updatedAccessoryData).subscribe({
      next: (response) => {
        this.handleSaveResponse(response);
      },
      error: (error) => {
        console.error("Error updating accessory:", error);
        this.notifier.notify('error', 'Error updating accessory');
      }
    });
  }

  private getFieldValue(id: string): any {
    const field = this.formFields.find(f => f.id === id);
    return field?.value ?? '';
  }

  private getSalesmanPrice(): number {
    const costPrice = Number(this.getFieldValue('costPrice'));
    const salesmanPrice = Number(this.getFieldValue('salesmanPrice'));
    return !salesmanPrice || salesmanPrice < costPrice ? costPrice : salesmanPrice;
  }

  private hasRequiredValues(): boolean {
    return this.formFields
      .filter((field) => field.required)
      .every((field) => String(field.value ?? field.defaultValue ?? '').trim().length > 0);
  }

  private handleSaveResponse(response: any): void {
    if (response?.code === 0) {
      this.notifier.notify('success', response.message || 'Accessory saved successfully.');
      this.router.navigate(['../../accessory'], { relativeTo: this.route });
    } else {
      this.notifier.notify('error', response?.message || 'Error saving accessory.');
    }
  }

  onCancel(): void {
    this.router.navigate(['../../accessory'], { relativeTo: this.route });
  }
}
